/**
 * pesce.ts — Categoría "Pescados y Mariscos" (10 productos demo).
 */
import { buildProduct } from "./_factory";
import type { Producto } from "@/types";

const CAT = {
  categoryId: "cat_pesce",
  skuPrefix: "PES",
  categoria: "Pescados y Mariscos",
} as const;

export const cat_pesce_products: Producto[] = [
  buildProduct({
    ...CAT,
    index: 0,
    nombre: "Filete de Tilapia 500g",
    marca: "Pacific Sea",
    modelo: "Fresco Congelado",
    origen: "Colombia",
    costo: 14500,
    precio: 22000,
    stock: 65,
    stockMinimo: 15,
    stockMaximo: 130,
    unidad: "paquete",
    ubicacionFisica: "Congelador P1, pescados",
    proveedor: "Pesquera del Pacífico",
    tiempoEntrega: "Diario",
    vencimientoMeses: 3,
    pesoNeto: "500g",
    descripcionLarga:
      "Filete de tilapia sin espinas, empaque individual al vacío. Carne blanca delicada, cocción rápida. Ideal para sudado, sancocho y apanado.",
    especificaciones: [
      { clave: "Corte", valor: "Filete sin piel" },
      { clave: "Conservación", valor: "Congelado -18°C" },
    ],
    imageKeywords: ["tilapia", "fish-fillet", "pescado"],
    color: "bg-sky-300",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 1,
    nombre: "Salmón Fresco 500g",
    marca: "Nordic Seafood",
    modelo: "Atlántico",
    origen: "Chile",
    costo: 38500,
    precio: 58500,
    stock: 40,
    stockMinimo: 10,
    stockMaximo: 80,
    unidad: "paquete",
    ubicacionFisica: "Congelador P1, pescados premium",
    proveedor: "Importadora Nordic Seafood",
    vencimientoMeses: 3,
    pesoNeto: "500g",
    descripcionLarga:
      "Salmón Atlántico fresco, rico en Omega-3. Corte uniforme, ideal para grill, horno o sushi. Trazabilidad certificada.",
    especificaciones: [
      { clave: "Omega-3", valor: "2.5g/100g" },
      { clave: "Origen", valor: "Chile" },
    ],
    imageKeywords: ["salmon", "fresh-fish", "salmon-fillet"],
    color: "bg-pink-500",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 2,
    nombre: "Camarón Precocido 500g",
    marca: "Pacific Sea",
    modelo: "Precocido 40/50",
    origen: "Ecuador",
    costo: 26500,
    precio: 39900,
    stock: 50,
    stockMinimo: 12,
    stockMaximo: 100,
    unidad: "paquete",
    ubicacionFisica: "Congelador P2, mariscos",
    proveedor: "Pesquera del Pacífico",
    vencimientoMeses: 6,
    pesoNeto: "500g",
    descripcionLarga:
      "Camarón precocido, listo para consumir. Calibre 40/50 por libra. Ideal para ensaladas, pasta, ceviche y cocteles.",
    especificaciones: [
      { clave: "Calibre", valor: "40/50" },
      { clave: "Estado", valor: "Precocido sin cabeza" },
    ],
    imageKeywords: ["shrimp", "camaron", "seafood"],
    color: "bg-pink-400",
    shape: "circle",
  }),
  buildProduct({
    ...CAT,
    index: 3,
    nombre: "Atén en Lomos Van Camp's 170g",
    marca: "Van Camp's",
    modelo: "Lomos en Agua",
    origen: "Colombia",
    costo: 5200,
    precio: 7900,
    stock: 150,
    stockMinimo: 32,
    stockMaximo: 280,
    unidad: "lata",
    ubicacionFisica: "Estante P3, conservas",
    proveedor: "Seatech International",
    vencimientoMeses: 36,
    pesoNeto: "170g",
    descripcionLarga:
      "Lomos de atún en agua, bajo en sodio. Fuente de proteína magra. Perfecto para ensaladas, pastas y sándwiches.",
    especificaciones: [
      { clave: "Conservación", valor: "En agua" },
      { clave: "Proteína", valor: "28g por lata" },
    ],
    imageKeywords: ["tuna-can", "atun", "canned-fish"],
    color: "bg-blue-600",
    shape: "circle",
  }),
  buildProduct({
    ...CAT,
    index: 4,
    nombre: "Róbalo Fresco 500g",
    marca: "Pacific Sea",
    modelo: "Entero Eviscerado",
    origen: "Colombia",
    costo: 19500,
    precio: 29500,
    stock: 35,
    stockMinimo: 10,
    stockMaximo: 70,
    unidad: "unidad",
    ubicacionFisica: "Congelador P1, pescados",
    proveedor: "Pesquera del Pacífico",
    vencimientoMeses: 3,
    pesoNeto: "500g",
    descripcionLarga:
      "Róbalo entero eviscerado, piel brillante y ojos claros. Carne blanca delicada, excelente para asar al horno entero o en filetes.",
    especificaciones: [{ clave: "Presentación", valor: "Entero eviscerado" }],
    imageKeywords: ["sea-bass", "robalo", "whole-fish"],
    color: "bg-sky-400",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 5,
    nombre: "Bacalao Salado 400g",
    marca: "Gadus Importados",
    modelo: "Noruego Desalado",
    origen: "Noruega",
    costo: 29800,
    precio: 44900,
    stock: 30,
    stockMinimo: 8,
    stockMaximo: 60,
    unidad: "paquete",
    ubicacionFisica: "Nevera P4, pescados premium",
    proveedor: "Importadora Nordic Seafood",
    vencimientoMeses: 6,
    pesoNeto: "400g",
    descripcionLarga:
      "Bacalao noruego salado, listo para desalar (24h en agua). Carne firme y sabor intenso. Clásico para platos españoles y portugueses.",
    especificaciones: [
      { clave: "Procedencia", valor: "Noruega" },
      { clave: "Preparación", valor: "Desalar 24h" },
    ],
    imageKeywords: ["salt-cod", "bacalao", "cod-fish"],
    color: "bg-stone-300",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 6,
    nombre: "Pulpo Cocido 500g",
    marca: "Gadus Importados",
    modelo: "Pulpo Gallego",
    origen: "España",
    costo: 35500,
    precio: 53900,
    stock: 25,
    stockMinimo: 6,
    stockMaximo: 50,
    unidad: "paquete",
    ubicacionFisica: "Congelador P2, mariscos",
    proveedor: "Importadora Nordic Seafood",
    vencimientoMeses: 6,
    pesoNeto: "500g",
    descripcionLarga:
      "Pulpo cocido gallego, listo para servir o asar. Textura tierna, sabor delicado. Ideal para pulpo a la gallega y tapas gourmet.",
    especificaciones: [{ clave: "Estado", valor: "Cocido listo" }],
    imageKeywords: ["octopus", "pulpo", "seafood-tapa"],
    color: "bg-rose-400",
    shape: "circle",
  }),
  buildProduct({
    ...CAT,
    index: 7,
    nombre: "Calamar Anillo Congelado 500g",
    marca: "Pacific Sea",
    origen: "Perú",
    costo: 15800,
    precio: 23900,
    stock: 55,
    stockMinimo: 12,
    stockMaximo: 110,
    unidad: "paquete",
    ubicacionFisica: "Congelador P2, mariscos",
    proveedor: "Pesquera del Pacífico",
    vencimientoMeses: 6,
    pesoNeto: "500g",
    descripcionLarga:
      "Anillos de calamar congelados, limpios y listos para apanar, freír o saltear. Textura tierna si se cocina al punto.",
    especificaciones: [
      { clave: "Corte", valor: "Anillo" },
      { clave: "Calibre", valor: "Mediano" },
    ],
    imageKeywords: ["calamari", "squid-rings", "seafood"],
    color: "bg-stone-200",
    shape: "circle",
  }),
  buildProduct({
    ...CAT,
    index: 8,
    nombre: "Trucha Arcoíris 1kg",
    marca: "Truchas del Oriente",
    modelo: "Entera Eviscerada",
    origen: "Colombia",
    costo: 18500,
    precio: 27900,
    stock: 40,
    stockMinimo: 10,
    stockMaximo: 80,
    unidad: "kg",
    ubicacionFisica: "Congelador P1, pescados",
    proveedor: "Truchas del Oriente S.A.S.",
    vencimientoMeses: 3,
    pesoNeto: "1kg",
    ventaPorPeso: true,
    descripcionLarga:
      "Trucha arcoíris de cultivo, Boyacá. Entera eviscerada, carne rosada y sabor fino. Ideal a la parrilla con limón y hierbas.",
    especificaciones: [
      { clave: "Cultivo", valor: "Acuicultura controlada" },
      { clave: "Presentación", valor: "Entera eviscerada" },
    ],
    imageKeywords: ["rainbow-trout", "trucha", "fresh-fish"],
    color: "bg-pink-300",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 9,
    nombre: "Mejillones Frescos 500g",
    marca: "Pacific Sea",
    modelo: "En su Concha",
    origen: "Chile",
    costo: 12500,
    precio: 18900,
    stock: 45,
    stockMinimo: 10,
    stockMaximo: 90,
    unidad: "paquete",
    ubicacionFisica: "Congelador P2, mariscos",
    proveedor: "Importadora Nordic Seafood",
    vencimientoMeses: 3,
    pesoNeto: "500g",
    descripcionLarga:
      "Mejillones frescos congelados, en su concha. Listos para cocinar al vapor, gratinados o en paellas. Sabor intenso marino.",
    especificaciones: [{ clave: "Presentación", valor: "En concha" }],
    imageKeywords: ["mussels", "mejillones", "shellfish"],
    color: "bg-stone-700",
    shape: "diamond",
  }),
];
